import { ColumnProps, DataTable } from "components/Table";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { PageContainer } from 'components/PageContainer'
import { Toolbar } from "components/Toolbar";
import { toast } from 'react-toastify';
import { costManagementService } from 'services/costManagementService';
import { MainContainer } from './styles';
import { FormTableLine } from './Components/FormTable';
import { dateToday } from "utils/dateToday";
import { ActiveFilters } from "components/ActiveFilters";
import { CustomPagination } from "components/CustomPagination";
import dayjs from "dayjs";
import { ISupportDriver } from "interfaces/IDriver";
import saveAs from "file-saver";
import PlanilhaIcon from "assets/planilhaIcon.svg";

export interface ISupportCost {
  id: number;
  defaultCost: number;
  costAdjustment: number;
  leaderCommission: number;
  supportDriver: ISupportDriver;
}


const defaultColumns: ColumnProps[] = [
  {
    name: "Motorista",
    align: "left",
    minWidth: "120px",
  },
  {
    name: "Placa",
    align: "center",
    minWidth: "80px",
  },
  {
    name: "Data de Entrega",
    align: "center",
    minWidth: "110px",
  },
  {
    name: "CD",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "Valor da Rota",
    align: "center",
    minWidth: "120px",
  },
  {
    name: "Ajuste",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "Total",
    align: "center",
    minWidth: "100px",
  },
  {
    name: "",
    minWidth: "40px",
  }
]

export const Support = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [originalData, setOriginalData] = useState<ISupportCost[]>([]);
  const [editedData, setEditedData] = useState<ISupportCost[]>([]);

  // Função para ler os search params da URL
  const [params, setParams] = useState(() => {
    const transshipPointIds = searchParams.getAll('transshipPointIds').map(id => Number(id)).filter(id => !isNaN(id));
    const teamIds = searchParams.getAll('teamIds').map(id => Number(id)).filter(id => !isNaN(id));
    const startDate = searchParams.get('startDate') || dateToday();
    const endDate = searchParams.get('endDate') || dateToday();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 15;
    const search = searchParams.get('search') || '';
    return {
      transshipPointIds: transshipPointIds.length > 0 ? transshipPointIds : undefined,
      teamIds: teamIds.length > 0 ? teamIds : undefined,
      startDate,
      endDate,
      page,
      pageSize,
      search,
      isActive: true,
    };
  });

  const [paginationData, setPaginationData] = useState<{
    totalPages: number;
    pageIndex: number;
  }>({
    totalPages: 1,
    pageIndex: 1
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      const data = await costManagementService.getSupportCost(params);

      const results = data.results || [];
      
      setOriginalData(results);
      setEditedData(results);
      setPaginationData({
        totalPages: data.totalPages || 1,
        pageIndex: data.pageIndex || 1
      });
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao buscar dados no sistema";
      toast.error(errorMessage);
      setOriginalData([]);
      setEditedData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditField = (id: number, field: keyof ISupportCost, value: any) => {
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleRollback = (id: number) => {
    const original = originalData.find(item => item.id === id);
    if (!original) return;
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...original } : item
    ));
  };

  const isRowEdited = (id: number) => {
    const orig = originalData.find(item => item.id === id);
    const edit = editedData.find(item => item.id === id);
    if (!orig || !edit) return false;
    return orig.defaultCost !== edit.defaultCost || orig.costAdjustment !== edit.costAdjustment;
  };

  const handleSaveBatch = async () => {
    if (!editedData || editedData.length === 0) return;
    const changed = editedData.filter(item => isRowEdited(item.id));
    if (changed.length === 0) return;
    try {
      setLoading(true);
      await costManagementService.updateSupportCostBatch({
        costs: changed.map(item => ({
          id: item.id,
          support_driver_id: item.supportDriver.id,
          default_cost: item.defaultCost,
          cost_adjustment: item.costAdjustment,
        }))
      });
      toast.success('Alterações salvas com sucesso!');
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao salvar alterações";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeParams = (newParamsOrFn: Function | typeof params) => {
    const updatedParams = typeof newParamsOrFn === 'function' ? newParamsOrFn(params) : newParamsOrFn;
    setParams(updatedParams);

    const newSearchParams = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => newSearchParams.append(key, v.toString()));
      } else if (value) {
        newSearchParams.set(key, value.toString());
      }
    });
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page: number) => {
    handleChangeParams({ ...params, page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    // Ao mudar pageSize, reseta para página 1
    handleChangeParams({ ...params, page: 1, pageSize });
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  // Mapeador de filtros para ActiveFilters
  const filterMapper = {
    startDate: {
      label: "Data início",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
    },
    endDate: {
      label: "Data fim",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate,
    },
    transshipPointIds: {
      label: "Centros de Distribuição",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.transshipPointIds || params.transshipPointIds.length === 0,
    },
    teamIds: {
      label: "Times",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.teamIds || params.teamIds.length === 0,
    }
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    if (filterKey in newParams) {
      if (filterKey === 'transshipPointIds' || filterKey === 'teamIds') {
        (newParams as any)[filterKey] = [];
      } else if (filterKey === 'startDate' || filterKey === 'endDate') {
        const today = dateToday();
        (newParams as any)['startDate'] = today;
        (newParams as any)['endDate'] = today;
      } else if (filterKey === 'search') {
        (newParams as any)[filterKey] = '';
      }

      handleChangeParams(newParams);
    }
  };

  const handleClearAllFilters = () => {
    const today = dateToday();
    const clearedParams = {
      startDate: today,
      endDate: today,
      transshipPointIds: [],
      teamIds: [],
      search: '',
      page: 1,
      pageSize: 15,
      isActive: true,
    };

    handleChangeParams(clearedParams);
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      const exportParams = {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
        transshipPointIds: params.transshipPointIds,
        teamIds: params.teamIds,
        isActive: params.isActive,
      };
      const response = await costManagementService.exportCosts('support', exportParams);
      const blob = new Blob([response], { type: 'application/ms-excel;charset=utf-8' });
      saveAs(blob, 'Suporte-Custos.xlsx');
    } catch (e) {
      toast.error("Houve um erro ao tentar baixar a planilha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pageContent">
      <PageContainer>
        <MainContainer>
          <div className='infos'>
            <h3>Suporte</h3>
            <Toolbar
              params={params}
              paramState={handleChangeParams}
              loadingState={loading}
              placeholder="Motorista ou placa"
              alignButtonsToEnd={true}
              page='support-costs'
              extraButtons={[
                {
                  text: 'Exportar planilha',
                  onClick: downloadFile,
                  disableState: loading,
                  type: 'outlined',
                  icon: <img src={PlanilhaIcon} alt="Exportar planilha" />
                },
                {
                  text: 'Salvar alterações',
                  onClick: handleSaveBatch,
                  disableState: !editedData || editedData.filter(item => isRowEdited(item.id)).length === 0 || loading,
                  type: 'filled'
                }
              ]}
            />
          </div>

          <ActiveFilters
            params={params}
            mapper={filterMapper}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            title="Filtros aplicados"
            hideWhenEmpty={true}
          />

          <DataTable loading={loading} columns={defaultColumns} contentHeight="40vh" loadingType="inline">
            {editedData && editedData.length > 0 ? editedData.map((element) => (
              <FormTableLine
                key={element.id}
                element={element}
                onEditField={(id, field, value) => handleEditField(id, field as keyof ISupportCost, value)}
                onRollback={handleRollback}
                isEdited={isRowEdited(element.id)}
              />
            )) : null}
          </DataTable>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <CustomPagination
              currentPage={paginationData.pageIndex}
              totalPages={paginationData.totalPages}
              onPageChange={handlePageChange}
              pageSize={params.pageSize}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
            />
          </Box>

          <div className="spacer" />
        </MainContainer>
      </PageContainer>
    </div>
  )
}

